"use client";

import React from "react";
import { observer } from "mobx-react-lite";
import StrongSymbolRow from "./StrongSymbolRow";
import { useStore } from "../services/store";
import { SectorMap } from "./symbols";
import {
  filterIdenticalSymbolGroups,
  filterSymbolsByMarketCap,
} from "./util-symbol-grouping";

function normalizeSectorGroups(groups) {
  if (Array.isArray(groups)) return groups;
  if (!groups || typeof groups !== "object") return [];

  return Object.entries(groups).map(([sector, symbols]) => ({
    sector,
    symbols: Array.isArray(symbols) ? symbols : [],
  }));
}

export function getTopSectorSymbolCount(top) {
  return normalizeSectorGroups(top).reduce(
    (sum, group) => sum + (group.symbols?.length || 0),
    0,
  );
}

export function getBottomSectorSymbolCount(bottom) {
  return getTopSectorSymbolCount(bottom);
}

function getTopPercentChange(item) {
  const value = Number(item?.percentChange);
  return Number.isFinite(value) ? value : 0;
}

function sortSectorEntries(entries, isPos) {
  return [...entries].sort(([, a], [, b]) => {
    const difference = getTopPercentChange(b) - getTopPercentChange(a);
    return isPos ? difference : -difference;
  });
}

function getSectorGroupStrength(entries, isPos) {
  const changes = entries.map(([, item]) => getTopPercentChange(item));
  return isPos ? Math.max(...changes) : Math.min(...changes);
}

function sortSectorGroupsByStrength(grouped, isPos) {
  const families = Object.entries(grouped).reduce((result, entry) => {
    const family = entry[0].split("-")[0];
    if (!result[family]) result[family] = [];
    result[family].push(entry);
    return result;
  }, {});

  return Object.entries(families)
    .sort(([familyA, groupsA], [familyB, groupsB]) => {
      const getFamilyStrength = (groups) => {
        const strengths = groups.map(([, entries]) =>
          getSectorGroupStrength(entries, isPos),
        );
        return isPos ? Math.max(...strengths) : Math.min(...strengths);
      };
      const strengthA = getFamilyStrength(groupsA);
      const strengthB = getFamilyStrength(groupsB);
      const difference = strengthB - strengthA;
      return (isPos ? difference : -difference) || familyA.localeCompare(familyB);
    })
    .flatMap(([family, groups]) =>
      groups.sort(([sectorA, entriesA], [sectorB, entriesB]) => {
        const parentOrder = Number(sectorB === family) - Number(sectorA === family);
        if (parentOrder) return parentOrder;

        return (
          (isPos ? 1 : -1) *
            (getSectorGroupStrength(entriesB, isPos) -
              getSectorGroupStrength(entriesA, isPos)) ||
          sectorA.localeCompare(sectorB)
        );
      }),
    );
}

function groupSymbolsBySector(entries) {
  const grouped = {};
  const seenBySector = {};

  entries.forEach(([symbol, item]) => {
    let matched = false;

    Object.entries(SectorMap).forEach(([sector, sectorSymbols]) => {
      if (!sectorSymbols.includes(symbol)) return;
      matched = true;

      if (!grouped[sector]) {
        grouped[sector] = [];
        seenBySector[sector] = new Set();
      }
      if (!seenBySector[sector].has(symbol)) {
        grouped[sector].push([symbol, item]);
        seenBySector[sector].add(symbol);
      }
    });

    if (!matched) {
      if (!grouped.other) {
        grouped.other = [];
        seenBySector.other = new Set();
      }
      if (!seenBySector.other.has(symbol)) {
        grouped.other.push([symbol, item]);
        seenBySector.other.add(symbol);
      }
    }
  });

  return grouped;
}

function filterParentSectorDuplicates(grouped) {
  const symbolsShownInSubsectors = new Set();

  Object.entries(grouped).forEach(([sector, entries]) => {
    if (!sector.includes("-")) return;

    entries.forEach(([symbol]) => {
      symbolsShownInSubsectors.add(symbol);
    });
  });

  if (!symbolsShownInSubsectors.size) return grouped;

  return Object.fromEntries(
    Object.entries(grouped)
      .map(([sector, entries]) => {
        if (sector.includes("-")) return [sector, entries];

        return [
          sector,
          entries.filter(([symbol]) => !symbolsShownInSubsectors.has(symbol)),
        ];
      })
      .filter(([, entries]) => entries.length > 0),
  );
}

function getSectorSpecificity(sector) {
  if (sector === "other") return [-1, -1];
  return [sector.split("-").length, sector.length];
}

function isMoreSpecificSector(candidate, current) {
  const [candidateDepth, candidateLength] = getSectorSpecificity(candidate);
  const [currentDepth, currentLength] = getSectorSpecificity(current);
  return (
    candidateDepth > currentDepth ||
    (candidateDepth === currentDepth && candidateLength > currentLength) ||
    (candidateDepth === currentDepth &&
      candidateLength === currentLength &&
      candidate.localeCompare(current) < 0)
  );
}

function deduplicateSectorGroups(grouped) {
  const groupsBySymbols = new Map();

  Object.entries(grouped).forEach(([sector, entries]) => {
    const signature = entries
      .map(([symbol]) => symbol)
      .sort()
      .join("|");
    const existing = groupsBySymbols.get(signature);

    if (!existing || isMoreSpecificSector(sector, existing[0])) {
      groupsBySymbols.set(signature, [sector, entries]);
    }
  });

  return Object.fromEntries(groupsBySymbols.values());
}

function SubsectorHeader({ children }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: "rgba(255,255,255,0.6)",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        marginBottom: 4,
        paddingLeft: 4,
        borderLeft: "2px solid rgba(255,255,255,0.2)",
      }}
    >
      {children}
    </div>
  );
}

function SectorSymbolRow({ sector, item, isPos, source }) {
  return (
    <StrongSymbolRow
      key={`${sector}-${item.symbol}`}
      sym={item.symbol}
      val={{
        sector,
        pattern: item.pattern || "",
        percentChange: item.percentChange,
      }}
      isPos={isPos}
      colCh={10}
      source={source}
    />
  );
}

const SectorMovePanel = observer(function SectorMovePanel({ data, source }) {
  const store = useStore();
  const isPos = source === "top";
  const displayGroups = normalizeSectorGroups(data?.[source]);

  if (!displayGroups.length) {
    return (
      <div
        style={{
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 12,
          color: "#9ca3af",
          padding: "8px 2px",
        }}
      >
        {isPos ? "(no top symbols over 5%)" : "(no bottom symbols under -5%)"}
      </div>
    );
  }

  const rawEntries = displayGroups.flatMap(({ symbols }) =>
    (symbols || [])
      .filter((item) => item?.symbol)
      .map((item) => [String(item.symbol).toLowerCase(), item]),
  );
  const filteredEntries = store.isMarketCapFilter
    ? filterSymbolsByMarketCap(rawEntries, store.marketCapMap)
    : rawEntries;
  const sortedGroupEntries = sortSectorGroupsByStrength(
    deduplicateSectorGroups(
      filterParentSectorDuplicates(groupSymbolsBySector(filteredEntries)),
    ),
    isPos,
  );
  const groupEntries = filterIdenticalSymbolGroups(sortedGroupEntries);

  return (
    <div
      className="font-mono text-sm whitespace-pre leading-5 near-white bg-near-black"
      style={{
        marginBottom: 8,
        borderLeft: `3px solid ${isPos ? "rgba(34,197,94,.9)" : "rgba(239,68,68,.9)"}`,
        padding: 8,
        paddingLeft: 12,
        borderTopLeftRadius: 8,
        borderBottomLeftRadius: 8,
        backgroundColor: isPos ? "rgba(20,83,45,.3)" : "rgba(76,29,29,.3)",
        boxShadow: "0 2px 6px rgba(0,0,0,.25)",
      }}
    >
      {groupEntries.map(([sector, sectorSymbols], index) => (
        <div key={sector}>
          {index > 0 && <div style={{ height: 8 }} />}
          <SubsectorHeader>{sector}</SubsectorHeader>
          {sortSectorEntries(sectorSymbols, isPos).map(([, item]) => (
            <SectorSymbolRow
              key={`${sector}-${item.symbol}`}
              sector={sector}
              item={item}
              isPos={isPos}
              source={source}
            />
          ))}
        </div>
      ))}
    </div>
  );
});

export const BottomSectorPanel = observer(function BottomSectorPanel({ data }) {
  return <SectorMovePanel data={data} source="bottom" />;
});

export default observer(function TopSectorPanel({ data }) {
  return <SectorMovePanel data={data} source="top" />;
});
